import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import CameraModal from '../components/CameraModal';
import ThemeSwitcher from '../components/ThemeSwitcher';
import UploadToast from '../components/UploadToast';
import { getProfiles, updateProfile, deleteProfile, uploadPhoto, uploadExcel,
  exportExcel, bulkUploadPhotos, createProfile, getClasses, uploadMasterImage,
  getUserSchools, addSchool, updateSchool, deleteSchool,
  getTemplates, uploadTemplateBg, bulkUploadSignatures, createTemplate, updateTemplate,
  proxyImage } from '../utils/api';
import DeliveryReport from '../components/DeliveryReport';
import CardExportModal from '../components/CardExportModal';
import api from '../utils/api';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';



function ProfileCard({ p, selected, onSelect, onEdit, onCamera, onMaster, onDelete, BASE_URL, locked }) {
  return (
    <div className={`profile-card${selected?' selected':''}`}>

      {/* Top row: checkbox + photo + sign + name + status */}
      <div className="profile-card-top">
        <input type="checkbox" checked={selected} onChange={onSelect}
          style={{accentColor:'var(--primary)',cursor:'pointer',width:16,height:16,flexShrink:0}}/>

        {/* Photo */}
        <div className="profile-card-photo">
          {p.photoPath
            ? <img src={`${BASE_URL}${p.photoPath}`} alt=""
                onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
            : null}
          <div className="photo-placeholder-card" style={{display:p.photoPath?'none':'flex'}}>👤</div>
        </div>

        {/* Name + meta */}
        <div className="profile-card-name-row">
          <div className="profile-card-name">{p.name||'—'}</div>
          <div className="profile-card-meta">
            <span className="badge badge-blue" style={{fontSize:'.62rem',padding:'1px 6px'}}>Job{p.jobNo||'—'}</span>
            <span className={`badge ${p.status==='unprocessed'?'badge-green':p.status==='inprocess'?'badge-orange':'badge-blue'}`}
              style={{fontSize:'.62rem',padding:'1px 6px'}}>{p.status}</span>
            {locked && <span style={{fontSize:'.68rem',color:'var(--warning)'}}>🔒</span>}
          </div>
        </div>

        {/* Signature thumbnail if available */}
        {p.signImagePath && (
          <img src={`${BASE_URL}${p.signImagePath}`} alt="sign" className="profile-card-sign"
            onError={e=>e.target.style.display='none'}/>
        )}
      </div>

      {/* Fields grid */}
      <div className="profile-card-fields">
        {[
          { label:'Sr No',      value:p.srNo,           mono:true  },
          { label:'PRN Code',   value:p.prnCode,        mono:true  },
          { label:'Mobile',     value:p.mobile                     },
          { label:'Class',      value:p.class                      },
          { label:'DOB',        value:p.dob                        },
          { label:'Blood',      value:p.bloodGroup                 },
          { label:'Father',     value:p.fatherName                 },
          { label:'Address',    value:p.address                    },
          { label:'Dept',       value:p.department                 },
          { label:'Desig.',     value:p.designation                },
          { label:'Emp Code',   value:p.employeeCode,   mono:true  },
          { label:'Aadhar',     value:p.aadhar,         mono:true  },
          { label:'PAN',        value:p.pan,            mono:true  },
          { label:'RFID',       value:p.rfidCode,       mono:true  },
          { label:'Sign Name',  value:p.signName                   },
          { label:'Auth Sign',  value:p.authSignName               },
        ].filter(f => f.value).map((f,i) => (
          <div key={i} className="profile-card-field">
            <span className="profile-card-field-label">{f.label}</span>
            <span className={`profile-card-field-value${f.mono?' mono':''}`}>{f.value}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="profile-card-actions">
        <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`}
          onClick={onEdit} style={locked?{opacity:.5}:{}} title={locked?'Locked':'Edit'}>
          {locked?'🔒':'✏️'} Edit
        </button>
        <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`}
          onClick={onCamera} style={locked?{opacity:.5}:{}} title="Camera">
          📷 Photo
        </button>
        <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`}
          onClick={onMaster} title="Images">
          🖼️ Images
        </button>
        {!locked && (
          <button className="btn btn-danger btn-xs" onClick={onDelete}>🗑️</button>
        )}
      </div>
    </div>
  );
}

const BASE_URL = process.env.REACT_APP_API_URL || '';
const STATUS_COLOR = { unprocessed:'badge-green', inprocess:'badge-orange', processed:'badge-blue' };

const ALL_FIELDS = [
  { key:'jobNo',              label:'Job No',               type:'text',     section:'Personal' },
  { key:'name',               label:'Name',                 type:'text',     section:'Personal' },
  { key:'prnCode',            label:'PRN Code',             type:'text',     section:'Personal' },
  { key:'dob',                label:'DOB',                  type:'text',     section:'Personal' },
  { key:'class',              label:'Class',                type:'text',     section:'Personal' },
  { key:'mobile',             label:'Mobile',               type:'text',     section:'Personal' },
  { key:'address',            label:'Address',              type:'textarea', section:'Personal' },
  { key:'bloodGroup',         label:'Blood Group',          type:'text',     section:'Personal' },
  { key:'fatherName',         label:'Father Name',          type:'text',     section:'Family' },
  { key:'fatherContactNo',    label:'Father Contact No',    type:'text',     section:'Family' },
  { key:'motherName',         label:'Mother Name',          type:'text',     section:'Family' },
  { key:'motherContactNo',    label:'Mother Contact No',    type:'text',     section:'Family' },
  { key:'emergencyName',      label:'Emergency Name',       type:'text',     section:'Family' },
  { key:'emergencyContactNo', label:'Emergency Contact No', type:'text',     section:'Family' },
  { key:'designation',        label:'Designation',          type:'text',     section:'Employee' },
  { key:'employeeCode',       label:'Employee Code',        type:'text',     section:'Employee' },
  { key:'doj',                label:'DOJ',                  type:'date',     section:'Employee' },
  { key:'dor',                label:'DOR',                  type:'date',     section:'Employee' },
  { key:'department',         label:'Department',           type:'text',     section:'Employee' },
  { key:'aadhar',             label:'Aadhar',               type:'text',     section:'IDs' },
  { key:'pan',                label:'PAN',                  type:'text',     section:'IDs' },
  { key:'rfidCode',           label:'RFID Code',            type:'text',     section:'IDs' },
  { key:'barCode',            label:'Bar Code',             type:'text',     section:'IDs' },
  { key:'qrCode',             label:'QR Code',              type:'text',     section:'IDs' },
  { key:'photoName',          label:'Photo Name',           type:'text',     section:'IDs' },
  { key:'signName',           label:'Sign Name',            type:'text',     section:'IDs' },
  { key:'authSignName',       label:'Auth Sign Name',       type:'text',     section:'IDs' },
];
const SECTION_ICONS = { Personal:'👤', Family:'👨‍👩‍👧', Employee:'💼', IDs:'🪪' };
const fmtDate = v => v ? new Date(v).toISOString().slice(0,10) : '';
const FIELDS_BY_SECTION = ALL_FIELDS.reduce((a,f)=>{ (a[f.section]=a[f.section]||[]).push(f); return a; },{});

// function downloadBlankTemplate(selectedKeys) {
//   const labels = selectedKeys.map(k=>ALL_FIELDS.find(f=>f.key===k)?.label||k);
//   const csv = labels.map(l=>`"${l}"`).join(',') + '\n' + labels.map(()=>'""').join(',') + '\n';
//   const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement('a'); a.href=url; a.download='profile_template.csv'; a.click();
//   URL.revokeObjectURL(url);
//   toast.success('Template downloaded!');
// }
function downloadBlankTemplate(selectedKeys) {
  const labels = selectedKeys.map(k => ALL_FIELDS.find(f => f.key === k)?.label || k);

  const ws = XLSX.utils.aoa_to_sheet([labels, labels.map(() => '')]);

  ws['!cols'] = labels.map(() => ({ wch: 20 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Profiles');

  XLSX.writeFile(wb, 'profile_template.xlsx');
  toast.success('Template downloaded!');
}


export default function UserDashboard() {
  
  const [templates,    setTemplates]    = useState([]);
const [showDesigner, setShowDesigner] = useState(false);
const [designerTmpl, setDesignerTmpl] = useState(null);

  const { user } = useAuth();
  const year = new Date().getFullYear();

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('list');

  // ── Upload toast ─────────────────────────────────────────────────────────────
  const [uploadToast, setUploadToast] = useState(null);
const [includePhotos, setIncludePhotos] = useState(false);

  // ── Schools ───────────────────────────────────────────────────────────────────
  const [schools,         setSchools]         = useState([]);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [editingSchool,   setEditingSchool]   = useState(null);
  const [schoolForm,      setSchoolForm]      = useState({ name:'', address:'', city:'', contactNo:'' });
  const [schoolSaving,    setSchoolSaving]    = useState(false);
// const [bulkUploadJob, setBulkUploadJob] = useState('');
const [bulkUploadSchool, setBulkUploadSchool] = useState('');
const [bulkUploadJob, setBulkUploadJob] = useState('');
  // ── My Images (user bg + signature for template) ─────────────────────────────
  const [myTemplates,       setMyTemplates]       = useState([]);
  const [selectedTmplId,    setSelectedTmplId]    = useState('');
  const [tmplBgUploading,   setTmplBgUploading]   = useState(false);
  const myBgRef    = useRef(null);

  // ── Profiles list ─────────────────────────────────────────────────────────────
  const [profiles,   setProfiles]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [search,     setSearch]     = useState('');
  // const [filterJob,  setFilterJob]  = useState('');
  const [filterClass,setFilterClass]= useState('');
  const [filterJob,  setFilterJob]  = useState('');
const [jobOptions, setJobOptions] = useState([]);
  const [sortBy,     setSortBy]     = useState('jobNo');
  const [sortOrder,  setSortOrder]  = useState('asc');
  const [showAll,    setShowAll]    = useState(true); // paging toggle
  // const [jobOptions, setJobOptions] = useState([]);
const [filterSchool,  setFilterSchool]  = useState('');
  const [classOptions,setClassOptions]=useState([]);
  const [loading,    setLoading]    = useState(false);
  const [filteredClassOptions, setFilteredClassOptions] = useState([]);
  // ── Selection ────────────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(()=>new Set());
  const [selectingAll,setSelectingAll]=useState(false);

  // ── Edit modal ───────────────────────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [saving,     setSaving]     = useState(false);

  // ── Master images modal ──────────────────────────────────────────────────────
  const [masterTarget,  setMasterTarget]  = useState(null); // profile
  const [masterType,    setMasterType]    = useState('sign'); // 'sign' | 'ref'
  const [masterUploading,setMasterUploading]=useState(false);
  const masterFileRef = useRef(null);

  // ── Photo modal ──────────────────────────────────────────────────────────────
  const [photoTarget,    setPhotoTarget]    = useState(null);
  const [showCamera,     setShowCamera]     = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const filePhotoRef = useRef(null);

  // ── Add profile ──────────────────────────────────────────────────────────────
  const [addForm,        setAddForm]        = useState({});
  const [addPhoto,       setAddPhoto]       = useState(null);
  const [addPhotoPreview,setAddPhotoPreview]= useState(null);
  const [addShowCamera,  setAddShowCamera]  = useState(false);
  const [addSaving,      setAddSaving]      = useState(false);
  const addFileRef = useRef(null);

  // ── Excel import ─────────────────────────────────────────────────────────────
  const [excelFile,     setExcelFile]     = useState(null);
  const [excelDrag,     setExcelDrag]     = useState(false);
  const [excelLoading,  setExcelLoading]  = useState(false);
  const [excelProgress, setExcelProgress] = useState(0);
  const [excelResult,   setExcelResult]   = useState(null);
  const [templateFields,setTemplateFields]= useState(()=>ALL_FIELDS.map(f=>f.key));
  const [showTemplate,  setShowTemplate]  = useState(false);
  const excelInputRef = useRef(null);

  // ── Excel export ─────────────────────────────────────────────────────────────
  const [showExport,     setShowExport]     = useState(false);
  const [exportFields,   setExportFields]   = useState(()=>ALL_FIELDS.map(f=>f.key));
  const [exportingExcel, setExportingExcel] = useState(false);

  // ── Card export ──────────────────────────────────────────────────────────────
  const [showCardExport, setShowCardExport] = useState(false);

  // ── Bulk upload (photos / sign1 / sign2) ────────────────────────────────────────
  const [bulkType, setBulkType] = useState('photos');
  const [bulkFiles,    setBulkFiles]    = useState([]);
  const [bulkDrag,     setBulkDrag]     = useState(false);
  const [bulkLoading,  setBulkLoading]  = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult,   setBulkResult]   = useState(null);
  const [bulkSign1,    setBulkSign1]    = useState([]);    // employee signatures (multiple files, matched by filename)
  const [bulkSign2,    setBulkSign2]    = useState(null);  // authorized signature (single, applied to whole job)
  const [bulkSign2Preview, setBulkSign2Preview] = useState(null);
  const [bulkSign2Job, setBulkSign2Job]    = useState('');    // jobNo for sign2
  const [bulkSignResult, setBulkSignResult] = useState(null);
  const [bulkSignLoading,  setBulkSignLoading]  = useState(false);
  const bulkInputRef = useRef(null);
  const sign1InputRef = useRef(null);
  const sign2InputRef = useRef(null);
  const [filterStatus, setFilterStatus] = useState('');
  const STATUS_OPTIONS = ['unprocessed', 'inprocess', 'processed'];
  // ── Fetch profiles ────────────────────────────────────────────────────────────
  const fetchProfiles = useCallback(async () => {
  setLoading(true);
  try {
    const params = { page, limit: showAll?10000:25, sortBy, order:sortOrder };
    if (search)       params.search      = search;
    // if (filterSchool) params.schoolName  = filterSchool;
    if (filterSchool) params.schoolName = filterSchool.trim();
    if (filterClass)  params.filterClass = filterClass;
    if (filterStatus)   params.status      = filterStatus;   
    console.log('fetchProfiles params:', params); // ← add this
    const { data } = await getProfiles(params);
    console.log('profiles returned:', data.total, data.profiles?.map(p=>p.schoolName)); // ← add this
    setProfiles(data.profiles); setTotal(data.total); setPages(data.pages);
  } catch { toast.error('Failed to load profiles'); }
  finally { setLoading(false); }
}, [search, page, filterJob, filterSchool, filterClass,filterStatus, sortBy, sortOrder, showAll]);
  // const refreshMeta = useCallback(() => {
  //   getProfiles({limit:10000,page:1}).then(({data})=>{
  //     const jobs=[...new Set(data.profiles.map(p=>p.jobNo).filter(Boolean))].sort((a,b)=>a-b);
  //     setJobOptions(jobs);
  //   }).catch(()=>{});
  //   getClasses().then(r=>setClassOptions(r.data)).catch(()=>{});
  // },[]);
  const refreshMeta = useCallback(() => {
  getProfiles({limit:10000,page:1}).then(({data})=>{
    // no more jobOptions needed
  }).catch(()=>{});
  getClasses().then(r=>setClassOptions(r.data)).catch(()=>{});
},[]);

  // Fetch schools
  // const fetchSchools = useCallback(async () => {
  //   try { const { data } = await getUserSchools(); setSchools(data); } catch {}
  // }, []);
  const fetchSchools = useCallback(async () => {
  try {
    // Schools are stored as schoolNames[] on the user object
    const names = user?.schoolNames || [];
    console.log('user in fetchSchools:', user?.username, user?.schoolNames);
    const schoolList = names.map((name, i) => ({ _id: i, name }));
    setSchools(schoolList);
  } catch(e) {
    console.error('fetchSchools failed:', e.message);
  }
}, [user]);
  useEffect(() => { fetchSchools(); }, [fetchSchools]);
useEffect(() => {
  if (filterSchool) {
    getProfiles({ limit: 10000, page: 1, schoolName: filterSchool.trim() })
      .then(({ data }) => {
        const classes = [...new Set(
          data.profiles.map(p => p.class).filter(Boolean)
        )].sort();
        setFilteredClassOptions(classes);
      })
      .catch(() => {});
  } else {
    // No school filter — show all classes
    setFilteredClassOptions(classOptions);
  }
}, [filterSchool, classOptions]);
  // Fetch templates for preview/export
  const fetchMyTemplates = useCallback(async () => {
    try {
      const { data } = await getTemplates();
      setMyTemplates(data);
      if (data.length && !selectedTmplId) setSelectedTmplId(data[0]._id);
    } catch {}
  }, [selectedTmplId]);
  useEffect(() => { fetchMyTemplates(); }, [fetchMyTemplates]);
const fetchTemplates = useCallback(async () => {
  try {
    const { data } = await getTemplates();
    setTemplates(data);
  } catch {}
}, []);
useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
// ───
  // useEffect(()=>{ setPage(1); },[search,filterJob,filterClass,sortBy,sortOrder,showAll]);
useEffect(()=>{ setPage(1); },[search,filterJob,filterSchool,filterClass,sortBy,sortOrder,showAll]);
  useEffect(()=>{ fetchProfiles(); },[fetchProfiles]);
  useEffect(()=>{ refreshMeta(); },[refreshMeta]);

  // ── Selection helpers ─────────────────────────────────────────────────────────
  const toggleSelect = id => setSelectedIds(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const toggleSelectAll = () => {
    const ids=profiles.map(p=>p._id); const allSel=ids.every(id=>selectedIds.has(id));
    setSelectedIds(prev=>{ const n=new Set(prev); allSel?ids.forEach(id=>n.delete(id)):ids.forEach(id=>n.add(id)); return n; });
  };
  const handleSelectAllPages = async () => {
    setSelectingAll(true);
    try {
      const params={limit:10000,page:1};
      if(filterJob)   params.jobNo=filterJob;
      if(filterClass) params.filterClass=filterClass;
      if(search)      params.search=search;
      const {data}=await getProfiles(params);
      setSelectedIds(new Set(data.profiles.map(p=>p._id)));
      toast.success(`Selected all ${data.total} profiles`);
    } catch { toast.error('Failed'); }
    finally { setSelectingAll(false); }
  };
  const selectByClass = cls => {
    const ids=profiles.filter(p=>p.class===cls).map(p=>p._id);
    setSelectedIds(prev=>{ const n=new Set(prev); ids.forEach(id=>n.add(id)); return n; });
    toast.success(`Selected ${ids.length} profiles in class ${cls}`);
  };
  const selectByJob = jobNo => {
    const ids=profiles.filter(p=>p.jobNo===jobNo).map(p=>p._id);
    setSelectedIds(prev=>{ const n=new Set(prev); ids.forEach(id=>n.add(id)); return n; });
    toast.success(`Selected ${ids.length} profiles in Job ${jobNo}`);
  };

  // ── School CRUD ──────────────────────────────────────────────────────────────
  const handleSaveSchool = async () => {
    if (!schoolForm.name.trim()) { toast.error('School name required'); return; }
    setSchoolSaving(true);
    try {
      if (editingSchool) {
        await updateSchool(editingSchool._id, schoolForm);
        toast.success('School updated');
      } else {
        await addSchool(schoolForm);
        toast.success('School added');
      }
      setShowSchoolModal(false);
      setEditingSchool(null);
      setSchoolForm({ name:'', address:'', city:'', contactNo:'' });
      fetchSchools();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSchoolSaving(false); }
  };
  const handleDeleteSchool = async (id, name) => {
    if (!window.confirm(`Delete school "${name}"?`)) return;
    try { await deleteSchool(id); toast.success('Deleted'); fetchSchools(); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  // ── Sort helper ───────────────────────────────────────────────────────────────
  const toggleSort = field => {
    if(sortBy===field) setSortOrder(o=>o==='asc'?'desc':'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };
  const SortBtn = ({field,label}) => (
    <span style={{cursor:'pointer',userSelect:'none',display:'inline-flex',alignItems:'center',gap:3}}
      onClick={()=>toggleSort(field)}>
      {label}
      <span style={{fontSize:'.6rem',opacity:sortBy===field?1:.3}}>{sortBy===field?(sortOrder==='asc'?'▲':'▼'):'⇅'}</span>
    </span>
  );

  // ── Edit ──────────────────────────────────────────────────────────────────────
  const openEdit = useCallback(p => {
    if(p.status!=='unprocessed'){ toast.error('🔒 Profile is locked. Ask admin to unlock.'); return; }
    const f={};
    ALL_FIELDS.forEach(({key,type})=>{ f[key]=type==='date'?fmtDate(p[key]):(p[key]||''); });
    setEditForm(f); setEditTarget(p);
  },[]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(editTarget._id,editForm);
      toast.success('✅ Profile saved!');
      setEditTarget(null); fetchProfiles();
    } catch(e){ toast.error(e.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  // ── Delete (user, only unprocessed) ──────────────────────────────────────────
  const handleDelete = async (id,name) => {
    if(!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try { await deleteProfile(id); toast.success('Deleted'); fetchProfiles(); refreshMeta(); }
    catch(e){ toast.error(e.response?.data?.message||'Delete failed'); }
  };

  // ── Master images ─────────────────────────────────────────────────────────────
  const doUploadMaster = async (file, type) => {
    if(!file||!masterTarget) return;
    setMasterUploading(true);
    setUploadToast({label:`Uploading ${type==='sign'?'signature':'reference'} image`,status:'uploading',progress:0});
    try {
      await uploadMasterImage(masterTarget._id,type,file,p=>setUploadToast(t=>t?{...t,progress:p}:t));
      setUploadToast({label:'Image saved!',status:'done'});
      toast.success(`${type==='sign'?'Signature':'Reference'} image saved!`);
      setMasterTarget(null); fetchProfiles();
    } catch { setUploadToast({label:'Upload failed',status:'error'}); toast.error('Upload failed'); }
    finally { setMasterUploading(false); }
  };

  // ── Photo upload ──────────────────────────────────────────────────────────────
  // const doUploadPhoto = useCallback(async file => {
  //   if(!file||!photoTarget) return;
  //   setPhotoUploading(true);
  //   setUploadToast({label:'Uploading photo',status:'uploading',progress:0,detail:file.name});
  //   try {
  //     const fd=new FormData(); fd.append('photo',file);
  //     await uploadPhoto(photoTarget._id,fd,p=>setUploadToast(t=>t?{...t,progress:p,detail:`${p}%`}:t));
  // const doUploadPhoto = useCallback(async (file, shape='circle') => {
  // if(!file||!photoTarget) return;
  // setPhotoUploading(true);
  // try {
  //   const fd = new FormData();
  //   fd.append('photo', file);
  //   fd.append('photoShape', shape);  // ← send shape to backend
  //   await uploadPhoto(photoTarget._id, fd, p=>setUploadToast(t=>t?{...t,progress:p}:t));
// const doUploadPhoto = useCallback(async (file, shape='circle') => {
//   if(!file||!photoTarget) return;
//   setPhotoUploading(true);
//   try {
//     const fd = new FormData();
//     fd.append('photo', file);
//     fd.append('photoShape', shape);  // ← send shape to backend
//     await uploadPhoto(photoTarget._id, fd, p=>setUploadToast(t=>t?{...t,progress:p}:t));

//       setUploadToast({label:'Photo saved!',status:'done',detail:`Saved to ${user.username}_${year}/`});
//       toast.success(`📸 Saved to ${user.username}_${year}/`);
//       setPhotoTarget(null); setShowCamera(false); fetchProfiles();
//     } catch { setUploadToast({label:'Upload failed',status:'error'}); toast.error('Photo upload failed'); }
//     finally { setPhotoUploading(false); }
//   },[photoTarget,user,year,fetchProfiles]);
const doUploadPhoto = useCallback(async (file, shape='circle', fromBrowse=false) => {
  if(!file||!photoTarget) return;
  setPhotoUploading(true);
  try {
    const fd = new FormData();
    fd.append('photo', file);
    fd.append('photoShape', shape);
    if(fromBrowse) fd.append('fromBrowse', 'true'); // ← tell backend to use original filename
    await uploadPhoto(photoTarget._id, fd, p=>setUploadToast(t=>t?{...t,progress:p}:t));
    setUploadToast({label:'Photo saved!',status:'done',detail:`Saved to ${user.username}_${year}/`});
    toast.success(`📸 Saved to ${user.username}_${year}/`);
    setPhotoTarget(null); setShowCamera(false); fetchProfiles();
  } catch(e) { 
    setUploadToast({label:'Upload failed',status:'error'}); 
    toast.error(e.response?.data?.message||'Photo upload failed'); 
  }
  finally { setPhotoUploading(false); }
},[photoTarget,user,year,fetchProfiles]);
// useEffect(()=>{ setFilterClass(''); },[filterJob]);
useEffect(()=>{ setFilterClass('');setFilterStatus('');    },[filterSchool]);
// ── Add profile ───────────────────────────────────────────────────────────────
  const handleAddProfile = async () => {
    if(!addForm.name&&!addForm.prnCode){ toast.error('Enter at least a Name or PRN Code'); return; }
    setAddSaving(true);
    setUploadToast({label:'Creating profile…',status:'uploading',progress:0});
    try {
      const fd=new FormData();
      Object.entries(addForm).forEach(([k,v])=>{ if(v!==''&&v!=null) fd.append(k,v); });
      if(addPhoto) fd.append('photo',addPhoto);
      const {data}=await createProfile(fd,p=>setUploadToast(t=>t?{...t,progress:p}:t));
      setUploadToast({label:'Profile created!',status:'done',detail:data.profile?.name||''});
      toast.success('Profile created!');
      setAddForm({}); setAddPhoto(null); setAddPhotoPreview(null);
      setTab('list'); fetchProfiles(); refreshMeta();
    } catch(e){
      const msg=e.response?.data?.message||'Failed';
      setUploadToast({label:'Create failed',status:'error',detail:msg}); toast.error(msg);
    } finally { setAddSaving(false); }
  };
  // const handleAddPhotoFile=file=>{ if(!file)return; setAddPhoto(file); const r=new FileReader(); r.onload=e=>setAddPhotoPreview(e.target.result); r.readAsDataURL(file); };
// const handleAddPhotoFile = (file, shape='circle') => {
//   if(!file) return;
//   setAddPhoto(file);
//   setAddForm(f=>({...f, photoShape: shape}));  // save shape in form
//   const r = new FileReader();
//   r.onload = e => setAddPhotoPreview(e.target.result);
//   r.readAsDataURL(file);
// };
const handleAddPhotoFile = (file, shape='circle') => {
  if(!file) return;
  setAddPhoto(file);
  setAddForm(f=>({...f, photoShape: shape}));  // save shape in form
  const r = new FileReader();
  r.onload = e => setAddPhotoPreview(e.target.result);
  r.readAsDataURL(file);
};


  // ── Excel import ──────────────────────────────────────────────────────────────
  const handleExcelDrop=e=>{ e.preventDefault(); setExcelDrag(false); const f=e.dataTransfer.files[0]; if(f&&(f.name.endsWith('.xlsx')||f.name.endsWith('.xls'))){setExcelFile(f);setExcelResult(null);}else toast.error('Drop .xlsx or .xls'); };
  // const handleExcelUpload=async()=>{
  //   if (!bulkUploadSchool) { toast.error('Select a school first'); return; }
  //   if(!excelFile){toast.error('Select a file first');return;}
  //   const { data } = await uploadExcel(excelFile, bulkUploadSchool, p => { ... });
  //   setExcelLoading(true); setExcelProgress(0); setExcelResult(null);
  //   setUploadToast({label:`Importing ${excelFile.name}`,status:'uploading',progress:0});
  //   try {
  //     const {data}=await uploadExcel(excelFile,p=>{setExcelProgress(p);setUploadToast(t=>t?{...t,progress:p,detail:p<100?`${p}%`:'Processing…'}:t);});
  //     setExcelResult({ok:true,data});
  //     setUploadToast({label:`Import complete — Job ${data.jobNo||''}`,status:'done',detail:`${data.inserted} inserted, ${data.updated} updated`});
  //     toast.success(`${data.inserted} inserted, ${data.updated} updated`);
  //     fetchProfiles(); refreshMeta();
  //   } catch(e){ const msg=e.response?.data?.message||'Failed'; setExcelResult({ok:false,msg}); setUploadToast({label:'Import failed',status:'error',detail:msg}); toast.error(msg); }
  //   finally { setExcelLoading(false); }
  // };
const handleExcelUpload = async () => {
  if (!bulkUploadSchool) { toast.error('Select a school first'); return; }
  if (!excelFile) { toast.error('Select a file first'); return; }
  setExcelLoading(true); setExcelProgress(0); setExcelResult(null);
  setUploadToast({label:`Importing ${excelFile.name}`, status:'uploading', progress:0});
  try {
    const {data} = await uploadExcel(excelFile, bulkUploadSchool, p => {
      setExcelProgress(p);
      setUploadToast(t => t ? {...t, progress:p, detail:p<100?`${p}%`:'Processing…'} : t);
    });
    setExcelResult({ok:true, data});
    setUploadToast({
      label:`Import complete — ${bulkUploadSchool}`,
      status:'done',
      detail:`${data.inserted} inserted, ${data.updated} updated`
    });
    toast.success(`${data.inserted} inserted, ${data.updated} updated`);
    fetchProfiles(); refreshMeta();
  } catch(e) {
    const msg = e.response?.data?.message || 'Failed';
    setExcelResult({ok:false, msg});
    setUploadToast({label:'Import failed', status:'error', detail:msg});
    toast.error(msg);
  } finally {
    setExcelLoading(false);
  }
};
  // ── Excel export ──────────────────────────────────────────────────────────────
  // const handleExport=async()=>{
  //   if(!exportFields.length){toast.error('Select at least one field');return;}
  //   setExportingExcel(true);
  //   const ids=selectedIds.size>0?[...selectedIds]:[];
  //   try {
  //     const {data}=await exportExcel(exportFields,ids);
  //     const url=URL.createObjectURL(new Blob([data],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}));
  //     const a=document.createElement('a'); const suf=ids.length?`_${ids.length}profiles`:'_all';
  //     a.href=url; a.download=`${user.username}_${year}${suf}_export.xlsx`; a.click(); URL.revokeObjectURL(url);
  //     toast.success(ids.length?`Exported ${ids.length} profiles!`:'All exported!'); setShowExport(false);
  //   } catch { toast.error('Export failed'); }
  //   finally { setExportingExcel(false); }
  // };
// const handleExport = async () => {
//   if (!exportFields.length) { toast.error('Select at least one field'); return; }
//   setExportingExcel(true);
//   const ids = selectedIds.size > 0 ? [...selectedIds] : [];
//   try {
//     const { data } = await exportExcel(exportFields, ids);
//     const excelBlob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

//     if (includePhotos && ids.length > 0) {
//       setUploadToast({ label: 'Preparing photos…', status: 'uploading', progress: 0 });

//       // Need full profile records (with photoPath) for the selected ids
//       const { data: allData } = await getProfiles({ limit: 10000, page: 1 });
//       const selectedProfiles = allData.profiles.filter(p => ids.includes(p._id));

//       const zip = new JSZip();
//       zip.file(`${user.username}_${year}_export.xlsx`, excelBlob);
//       const photosFolder = zip.folder('photos');

//       let done = 0;
//       for (const p of selectedProfiles) {
//         if (p.photoPath) {
//           try {
//             const res = await fetch(`${BASE_URL}${p.photoPath}`);
//             const blob = await res.blob();
//             const ext = p.photoPath.split('.').pop() || 'jpg';
//             const safeName = (p.name || p.prnCode || p.srNo || p._id).replace(/[^a-z0-9]/gi, '_');
//             photosFolder.file(`${safeName}.${ext}`, blob);
//           } catch { /* skip a failed photo, don't kill the whole export */ }
//         }
//         done++;
//         setUploadToast(t => t ? { ...t, progress: Math.round((done / selectedProfiles.length) * 100), detail: 'Fetching photos…' } : t);
//       }

//       const zipBlob = await zip.generateAsync({ type: 'blob' }, meta => {
//         setUploadToast(t => t ? { ...t, progress: Math.round(meta.percent), detail: 'Zipping…' } : t);
//       });

//       const url = URL.createObjectURL(zipBlob);
//       const a = document.createElement('a');
//       a.href = url;
//       a.download = `${user.username}_${year}_${ids.length}profiles_export.zip`;
//       a.click();
//       URL.revokeObjectURL(url);
//       setUploadToast({ label: 'Export complete!', status: 'done', detail: `${ids.length} profiles + photos` });
//       toast.success(`Exported ${ids.length} profiles with photos!`);
//     } else {
//       const url = URL.createObjectURL(excelBlob);
//       const a = document.createElement('a');
//       const suf = ids.length ? `_${ids.length}profiles` : '_all';
//       a.href = url;
//       a.download = `${user.username}_${year}${suf}_export.xlsx`;
//       a.click();
//       URL.revokeObjectURL(url);
//       toast.success(ids.length ? `Exported ${ids.length} profiles!` : 'All exported!');
//     }
//     setShowExport(false);
//   } catch {
//     toast.error('Export failed');
//     setUploadToast({ label: 'Export failed', status: 'error' });
//   } finally {
//     setExportingExcel(false);
//   }
// };
const handleExport = async () => {
  if (!exportFields.length) { toast.error('Select at least one field'); return; }
  setExportingExcel(true);
  const ids = selectedIds.size > 0 ? [...selectedIds] : [];

  try {
    const { data } = await exportExcel(exportFields, ids);
    const excelBlob = new Blob(
      [data],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );

    if (includePhotos && ids.length > 0) {
      setUploadToast({ label: 'Preparing photos…', status: 'uploading', progress: 0 });

      const { data: allData } = await getProfiles({ limit: 10000, page: 1 });
      const selectedProfiles = allData.profiles.filter(p => ids.includes(p._id));

      const zip = new JSZip();
      zip.file(`${user.username}_${year}_export.xlsx`, excelBlob);
      const photosFolder = zip.folder('photos');

      let done = 0;
      for (const p of selectedProfiles) {
        if (p.photoPath) {
          try {
            const res = await proxyImage(p.photoPath);
            // const ext = p.photoPath.split('.').pop() || 'jpg';
            // const safeName = (p.name || p.prnCode || p.srNo || p._id).replace(/[^a-z0-9]/gi, '_');
            // photosFolder.file(`${safeName}.${ext}`, res.data);
            const fileName = p.photoPath.split('/').pop(); // e.g. "IMG_0003.jpg"
photosFolder.file(fileName, res.data);
          } catch (err) {
            console.error('Photo fetch failed for', p.photoPath, err);
          }
        }
        done++;
        setUploadToast(t => t
          ? { ...t, progress: Math.round((done / selectedProfiles.length) * 100), detail: 'Fetching photos…' }
          : t
        );
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' }, meta => {
        setUploadToast(t => t ? { ...t, progress: Math.round(meta.percent), detail: 'Zipping…' } : t);
      });

      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${user.username}_${year}_${ids.length}profiles_export.zip`;
      a.click();
      URL.revokeObjectURL(url);

      setUploadToast({ label: 'Export complete!', status: 'done', detail: `${ids.length} profiles + photos` });
      toast.success(`Exported ${ids.length} profiles with photos!`);
    } else {
      const url = URL.createObjectURL(excelBlob);
      const a = document.createElement('a');
      const suf = ids.length ? `_${ids.length}profiles` : '_all';
      a.href = url;
      a.download = `${user.username}_${year}${suf}_export.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success(ids.length ? `Exported ${ids.length} profiles!` : 'All exported!');
    }

    setShowExport(false);
  } catch (err) {
    console.error('Export failed:', err);
    toast.error('Export failed');
    setUploadToast({ label: 'Export failed', status: 'error' });
  } finally {
    setExportingExcel(false);
  }
};
  // ── Bulk photos ───────────────────────────────────────────────────────────────
  const addBulkFiles=files=>{ const imgs=Array.from(files).filter(f=>['.jpg','.jpeg','.png','.webp'].some(x=>f.name.toLowerCase().endsWith(x))); if(imgs.length<files.length)toast.error('Some skipped — only JPG/PNG/WEBP'); setBulkFiles(prev=>{const n=new Set(prev.map(f=>f.name));return[...prev,...imgs.filter(f=>!n.has(f.name))];});setBulkResult(null); };
  const handleBulkDrop=e=>{e.preventDefault();setBulkDrag(false);addBulkFiles(e.dataTransfer.files);};
  // const handleBulkUpload = async () => {
  //   if (bulkType==='photos' && !bulkFiles.length)  { toast.error('Add photos first'); return; }
  //   if (bulkType==='sign1'  && !bulkSign1.length)  { toast.error('Add signature files first'); return; }
  //   if (bulkType==='sign2'  && !bulkSign2)         { toast.error('Choose a signature image'); return; }
  //   setBulkLoading(true); setBulkProgress(0); setBulkResult(null); setBulkSignResult(null);
  //   try {
  //     if (bulkType === 'photos') {
  //       setUploadToast({label:`Uploading ${bulkFiles.length} photos…`, status:'uploading', progress:0});
  //       const {data} = await bulkUploadPhotos(bulkFiles, p => {
  //         setBulkProgress(p);
  //         setUploadToast(t => t ? {...t, progress:p, detail:`${p}%`} : t);
  //       });
  //       setBulkResult({ok:true, data});
  //       setUploadToast({label:'Photos uploaded!', status:'done', detail:`${data.saved} saved`});
  //     } else if (bulkType === 'sign1') {
  //       setBulkSignLoading(true);
  //       setUploadToast({label:`Uploading ${bulkSign1.length} signatures…`, status:'uploading', progress:0});
  //       const {data} = await bulkUploadSignatures(bulkSign1, 'sign1', null,
  //         p => setUploadToast(t => t ? {...t, progress:p} : t));
  //       setBulkSignResult({sign1: data});
  //       setUploadToast({label:'Signatures uploaded!', status:'done', detail:`${data.saved} matched`});
  //     } else if (bulkType === 'sign2') {
  //       setUploadToast({label:'Uploading authorized signature…', status:'uploading', progress:0});
  //       const {data} = await bulkUploadSignatures([bulkSign2], 'sign2', null);
  //       setBulkSignResult({sign2: data});
  //       setUploadToast({label:'Authorized signature saved!', status:'done'});
  //     }
  //     fetchProfiles();
  //   } catch(e) {
  //     const msg = e.response?.data?.message || 'Failed';
  //     setUploadToast({label:'Upload failed', status:'error', detail:msg});
  //     toast.error(msg);
  //   } finally { setBulkLoading(false); setBulkSignLoading(false); }
//   // };
//   const handleBulkUpload = async () => {
//     if (!bulkUploadJob) { toast.error('Select a Job first'); return; }
// const {data} = await bulkUploadPhotos(bulkFiles, bulkUploadJob, p => {...});
//   if (!bulkFiles.length) { toast.error('Add files first'); return; }
//   setBulkLoading(true); setBulkProgress(0); setBulkResult(null); setBulkSignResult(null);
//   setUploadToast({label:`Uploading ${bulkFiles.length} files…`, status:'uploading', progress:0});
//   try {
//     const {data} = await bulkUploadPhotos(bulkFiles, p => {
//       setBulkProgress(p);
//       setUploadToast(t => t ? {...t, progress:p, detail:`${p}%`} : t);
//     });
//     setBulkResult({ok:true, data});
//     // setUploadToast({label:'Upload complete!', status:'done', detail:`${data.saved} saved`});
//     setUploadToast({label:`Photo Job ${data.photoJobNo} uploaded!`, status:'done', detail:`${data.saved} saved`});
//     fetchProfiles();
//   } catch(e) {
//     const msg = e.response?.data?.message || 'Failed';
//     setBulkResult({ok:false, msg});
//     setUploadToast({label:'Upload failed', status:'error', detail:msg});
//     toast.error(msg);
//   } finally { setBulkLoading(false); }
// };
// const handleBulkUpload = async () => {
//   // if (!bulkUploadJob) { toast.error('Select a Job first'); return; }
//   if (!bulkFiles.length) { toast.error('Add files first'); return; }
//   setBulkLoading(true); setBulkProgress(0); setBulkResult(null); setBulkSignResult(null);
//   setUploadToast({label:`Uploading ${bulkFiles.length} files…`, status:'uploading', progress:0});
//   try {
//     const {data} = await bulkUploadPhotos(bulkFiles, bulkUploadJob, p => {
//       setBulkProgress(p);
//       setUploadToast(t => t ? {...t, progress:p, detail:`${p}%`} : t);
//     });
//     setBulkResult({ok:true, data});
//     setUploadToast({label:`Photo Job ${data.photoJobNo} uploaded!`, status:'done', detail:`${data.saved} saved`});
//     fetchProfiles();
//   } catch(e) {
//     const msg = e.response?.data?.message || 'Failed';
//     setBulkResult({ok:false, msg});
//     setUploadToast({label:'Upload failed', status:'error', detail:msg});
//     toast.error(msg);
//   } finally { setBulkLoading(false); }
// };
const handleBulkUpload = async () => {
  if (!bulkUploadSchool) { toast.error('Select a school first'); return; }
  if (!bulkFiles.length) { toast.error('Add files first'); return; }
  setBulkLoading(true); setBulkProgress(0); setBulkResult(null); setBulkSignResult(null);
  setUploadToast({label:`Uploading ${bulkFiles.length} files…`, status:'uploading', progress:0});
  try {
    const {data} = await bulkUploadPhotos(bulkFiles, bulkUploadSchool, p => {
      setBulkProgress(p);
      setUploadToast(t => t ? {...t, progress:p, detail:`${p}%`} : t);
    });
    setBulkResult({ok:true, data});
    setUploadToast({label:`Photos uploaded!`, status:'done', detail:`${data.saved} saved`});
    fetchProfiles();
  } catch(e) {
    const msg = e.response?.data?.message || 'Failed';
    setBulkResult({ok:false, msg});
    setUploadToast({label:'Upload failed', status:'error', detail:msg});
    toast.error(msg);
  } finally { setBulkLoading(false); }
};
  const handleBulkSign2Pick = (file) => {
    if (!file) return;
    setBulkSign2(file);
    const reader = new FileReader();
    reader.onload = e => setBulkSign2Preview(e.target.result);
    reader.readAsDataURL(file);
  };

  const fmtSize=b=>b<1048576?`${(b/1024).toFixed(0)} KB`:`${(b/1048576).toFixed(1)} MB`;
  const toggleTField=k=>setTemplateFields(f=>f.includes(k)?f.filter(x=>x!==k):[...f,k]);
  const toggleEField=k=>setExportFields(f=>f.includes(k)?f.filter(x=>x!==k):[...f,k]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div className="page-header-text">
          <h1> <span style={{color:'var(--primary)'}}>👋 Welcome, {user?.username}</span></h1>
          {/* <p>{total} profile{total!==1?'s':''} · Folder: <code style={{fontFamily:'monospace',fontSize:'.8rem',background:'var(--bg3)',padding:'1px 6px',borderRadius:4}}>{user?.username}_{year}</code></p> */}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button className="btn btn-outline btn-sm" onClick={()=>setShowSchoolModal(true)}>🏫 Schools</button>
          <ThemeSwitcher />
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-row">
        <button className={`tab-btn${tab==='list'    ?' active':''}`} onClick={()=>setTab('list')}>📋 My Profiles {total>0&&<span className="tab-count">{total}</span>}</button>
        <button className={`tab-btn${tab==='excel'   ?' active':''}`} onClick={()=>setTab('excel')}>📊 Import Excel</button>
        <button className={`tab-btn${tab==='photos'  ?' active':''}`} onClick={()=>setTab('photos')}>🖼️ Bulk Photos</button>
        <button className={`tab-btn${tab==='myimages'?' active':''}`} onClick={()=>setTab('myimages')}>🎨 My Images</button>
        <button className={`tab-btn${tab==='add'     ?' active':''}`} onClick={()=>setTab('add')}>➕ Add Profile</button>
        <button className={`tab-btn${tab==='delivery'?' active':''}`} onClick={()=>setTab('delivery')}>📦 Delivery</button>
      </div>

      {/* ══ PROFILES LIST ══════════════════════════════════════════════════════ */}
      {tab==='list' && (
        <div className="card">
          {/* Filters */}
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8,alignItems:'center'}}>
  <input className="search-input" style={{flex:'0 1 140px',minWidth:100}} placeholder="🔍 Search…"
              value={search} onChange={e=>setSearch(e.target.value)}/>
            {/* <select className="search-input" style={{maxWidth:150}} value={filterJob} onChange={e=>setFilterJob(e.target.value)}>
              <option value="">All Jobs</option>
              {jobOptions.map(j=><option key={j} value={j}>Job {j}</option>)}
            </select>
            <select className="search-input" style={{maxWidth:130}} value={filterClass} onChange={e=>setFilterClass(e.target.value)}>
              <option value="">All Classes</option>
              {classOptions.map(c=><option key={c} value={c}>{c}</option>)}
            </select> */}
         <select className="search-input" style={{maxWidth:160,minWidth:100,fontSize:'.78rem'}} value={filterSchool} onChange={e=>setFilterSchool(e.target.value)}>
  <option value="">All Schools</option>
  {schools.map(s=><option key={s._id} value={s.name}>{s.name}</option>)}
</select>
<select className="search-input" style={{maxWidth:140}} 
  value={filterStatus} 
  onChange={e => setFilterStatus(e.target.value)}>
  <option value="">All Statuses</option>
  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
</select>
{/* <select className="search-input" style={{maxWidth:100,minWidth:80,fontSize:'.78rem'}} value={filterClass} onChange={e=>setFilterClass(e.target.value)}>
  <option value="">All Class</option>
  {classOptions.map(c=><option key={c} value={c}>{c}</option>)}
</select> */}
           <select className="search-input" style={{maxWidth:130}} value={filterClass} 
  onChange={e=>{ setFilterClass(e.target.value); }}>
  <option value="">All Classes</option>
  {filteredClassOptions.map(c=><option key={c} value={c}>{c}</option>)}
</select>

 <button className="btn btn-outline btn-sm" onClick={()=>setShowAll(s=>!s)} title={showAll?'Enable paging':'Show all'}>
              {showAll?'📄 Paged':'📋 All'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={fetchProfiles}>🔄</button>
            <button className="btn btn-primary btn-sm" onClick={()=>setShowExport(true)}>
              {selectedIds.size>0?`⬇️ (${selectedIds.size})`:'⬇️ Export'}
            </button>
            <button className="btn btn-outline btn-sm" onClick={()=>setShowCardExport(true)} title="Export ID cards as PDF">
              🖨️ Cards PDF
            </button>
          </div>

          {/* Selection bar */}
          {selectedIds.size>0 && (
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',padding:'8px 12px',marginBottom:8,background:'rgba(var(--primary-rgb),.1)',border:'1px solid rgba(var(--primary-rgb),.25)',borderRadius:'var(--radius-sm)'}}>
              <span style={{fontWeight:700,fontSize:'.85rem',color:'var(--primary)'}}>✅ {selectedIds.size} selected</span>
              {selectedIds.size<total && (
                <button className="btn btn-outline btn-sm" onClick={handleSelectAllPages} disabled={selectingAll}>
                  {selectingAll?'⏳':`All ${total}`}
                </button>
              )}
              {/* Select by class */}
              {classOptions.length>0 && (
                <select className="search-input" style={{maxWidth:140,padding:'4px 8px',fontSize:'.78rem'}}
                  defaultValue=""
                  onChange={e=>{if(e.target.value){selectByClass(e.target.value);e.target.value='';}}}
                >
                  <option value="" disabled>+ Select by class</option>
                  {classOptions.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              )}
              {/* Select by job */}
              {jobOptions.length>0 && (
                <select className="search-input" style={{maxWidth:130,padding:'4px 8px',fontSize:'.78rem'}}
                  defaultValue=""
                  onChange={e=>{if(e.target.value){selectByJob(parseInt(e.target.value));e.target.value='';}}}
                >
                  <option value="" disabled>+ Select by job</option>
                  {jobOptions.map(j=><option key={j} value={j}>Job {j}</option>)}
                </select>
              )}
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelectedIds(new Set())}>✕ Clear</button>
            </div>
          )}

          {loading ? (
            <div className="loading"><div className="spinner"/><p style={{marginTop:12}}>Loading…</p></div>
          ) : profiles.length===0 ? (
            <div className="empty-state"><div className="empty-state-icon">📭</div><h3>No profiles found</h3><p>{search?'Try a different search.':'Import Excel or Add Profile to get started.'}</p></div>
          ) : (
            <>
              {/* Job-grouped view — default when sorting by job */}
              {sortBy==='jobNo' && !filterJob && profiles.length > 0 && (() => {
                // Group: unprocessed first within each job, then processed at end
                const grouped = {};
                profiles.forEach(p=>{
                  const key = `${p.jobNo||0}_${p.status==='unprocessed'?0:1}`;
                  if(!grouped[key]) grouped[key]={jobNo:p.jobNo,status:p.status,profiles:[]};
                  grouped[key].profiles.push(p);
                });
                // Sort keys: job asc, unprocessed before processed
                const keys = Object.keys(grouped).sort((a,b)=>{
                  const [ja,sa]=a.split('_').map(Number);
                  const [jb,sb]=b.split('_').map(Number);
                  if(ja!==jb) return ja-jb;
                  return sa-sb;
                });
                return (
                  <div>
                    {keys.map(key=>{

                      const grp = grouped[key];
                      // inside the .map(key => { const grp = grouped[key]; ... }) block
// before the return
const hasAnySign = grp.profiles.some(p => p.signImagePath);

  return (
                        <div key={key}>
                          <div className="job-group-header">
                            <span>📋</span>
                            <span className="job-badge">Job {grp.jobNo||'—'}</span>
                            <span style={{color:'var(--text-muted)',fontWeight:400,fontSize:'.76rem'}}>{grp.profiles.length} profile{grp.profiles.length!==1?'s':''}</span>
                            <span className={`status-badge badge ${grp.status==='unprocessed'?'badge-green':grp.status==='inprocess'?'badge-orange':'badge-blue'}`}>
                              {grp.status}
                            </span>
                          </div>
                          <div className="profile-table-wrap">
                          <div className="table-wrap" style={{marginBottom:4}}>
                            <table>
                              <thead>
                                <tr>
                                  <th style={{width:36}}><input type="checkbox"
                                    style={{accentColor:'var(--primary)',cursor:'pointer',width:15,height:15}}
                                    checked={grp.profiles.every(p=>selectedIds.has(p._id))}
                                    onChange={()=>{
                                      const ids=grp.profiles.map(p=>p._id);
                                      const allSel=ids.every(id=>selectedIds.has(id));
                                      setSelectedIds(prev=>{const n=new Set(prev);allSel?ids.forEach(id=>n.delete(id)):ids.forEach(id=>n.add(id));return n;});
                                    }}/></th>
                                  <th>Sr</th>
                                  <th>Photo</th>
{hasAnySign && <th>Sign</th>}  {/* ✅ only show header if any profile has a sign */}
<th>Name</th>
                                  
                                   {/* <th>Class</th> */}
                                  <th>Mobile</th><th>Status</th><th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {grp.profiles.map(p=>{
                                  const locked=p.status!=='unprocessed';
                                  return (
                                    <tr key={p._id} style={{background:selectedIds.has(p._id)?'rgba(var(--primary-rgb),.07)':undefined}}>
                                      <td><input type="checkbox" checked={selectedIds.has(p._id)} onChange={()=>toggleSelect(p._id)} style={{accentColor:'var(--primary)',cursor:'pointer',width:15,height:15}}/></td>
                                      <td className="td-mono" style={{color:'var(--primary)',fontWeight:700}}>{p.srNo||'—'}</td>
                                      {/* <td>
                                        {p.photoPath?<img className="photo-thumb" src={`${BASE_URL}${p.photoPath}`} alt="" onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>:null}
                                        <div className="photo-placeholder" style={{display:p.photoPath?'none':'flex'}}>👤</div>
                                      </td> */}
                                      <td>
  {p.photoPath
    ? <img className="photo-thumb" src={`${BASE_URL}${p.photoPath}`} alt=""
    style={{borderRadius: p.photoShape==='square'?'8px':'50%'}}
        onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
    : null}
  <div className="photo-placeholder" style={{display:p.photoPath?'none':'flex'}}>👤</div>
</td>
{/* <td>
  {p.signImagePath
    ? <img src={`${BASE_URL}${p.signImagePath}`} alt="sign"
        style={{width:60,height:40,objectFit:'contain',background:'#fff',border:'1px solid var(--border)',borderRadius:4,padding:2}}
        onError={e=>e.target.style.display='none'}/>
    : <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>{p.signName||'—'}</span>
  }
</td> */}
{hasAnySign && (
  <td>
    {p.signImagePath
      ? <img src={`${BASE_URL}${p.signImagePath}`} alt="sign"
          style={{width:60,height:40,objectFit:'contain',background:'#fff',border:'1px solid var(--border)',borderRadius:4,padding:2}}
          onError={e=>e.target.style.display='none'}/>
      : <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>—</span>
    }
  </td>
)}
<td className="td-bold">{p.name||'—'}</td>

                                      
                                      {/* <td>{p.class||'—'}</td> */}
                                      <td>{p.mobile||'—'}</td>
                                      <td><span className={`badge ${STATUS_COLOR[p.status]||'badge-orange'}`}>{p.status}</span></td>
                                      <td>
                                        <div className="action-btns">
                                          <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`} onClick={()=>openEdit(p)} style={locked?{opacity:.5}:{}} title={locked?'Locked':'Edit'}>{locked?'🔒':'✏️'}</button>
                                          <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`} style={locked?{opacity:.5}:{}} title={locked?'Photo locked':'Take photo'} onClick={()=>{if(locked){toast.error('🔒 Photo locked.');return;}setPhotoTarget(p);setShowCamera(true);}}>📷</button>
                                          <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`} style={locked?{opacity:.5}:{}} title="Master images" onClick={()=>{if(locked){toast.error('🔒 Locked.');return;}setMasterTarget(p);setMasterType('sign');}}>🖼️</button>
                                          {!locked&&<button className="btn btn-danger btn-xs" onClick={()=>handleDelete(p._id,p.name)}>🗑️</button>}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          </div>
                          <div className="profile-cards-wrap">
  {grp.profiles.map(p => {
    const locked = p.status !== 'unprocessed';
    return (
      <ProfileCard
        key={p._id}
        p={p}
        selected={selectedIds.has(p._id)}
        locked={locked}
        BASE_URL={BASE_URL}
        onSelect={()=>toggleSelect(p._id)}
        onEdit={()=>openEdit(p)}
        onCamera={()=>{ if(locked){toast.error('🔒 Photo locked.');return;} setPhotoTarget(p);setShowCamera(true); }}
        onMaster={()=>{ if(locked){toast.error('🔒 Locked.');return;} setMasterTarget(p);setMasterType('sign'); }}
        onDelete={()=>handleDelete(p._id,p.name)}
      />
    );
  })}
</div>

                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Flat view when filtering by specific job or sorting by other field */}
              {(sortBy!=='jobNo' || filterJob) && (
                <>
                  {/* <div className="table-wrap"> */}
                    <div className="profile-table-wrap">
<div className="table-wrap"><table>
                      <thead>
                        <tr>
                          <th style={{width:36}}>
                            <input type="checkbox"
                              style={{accentColor:'var(--primary)',cursor:'pointer',width:15,height:15}}
                              checked={profiles.length>0&&profiles.every(p=>selectedIds.has(p._id))}
                              onChange={toggleSelectAll}/>
                          </th>
                          <th><SortBtn field="srNo"  label="Sr"/></th>
                          <th><SortBtn field="jobNo" label="Job"/></th>
                          <th>Photo</th>
                          <th><SortBtn field="name"  label="Name"/></th>
                          <th><SortBtn field="class" label="Class"/></th>
                          <th>Mobile</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profiles.map(p=>{
                          const locked=p.status!=='unprocessed';
                          return (
                            <tr key={p._id} style={{background:selectedIds.has(p._id)?'rgba(var(--primary-rgb),.07)':undefined}}>
                              <td><input type="checkbox" checked={selectedIds.has(p._id)} onChange={()=>toggleSelect(p._id)} style={{accentColor:'var(--primary)',cursor:'pointer',width:15,height:15}}/></td>
                              <td className="td-mono" style={{color:'var(--primary)',fontWeight:700}}>{p.srNo||'—'}</td>
                              <td><span className="badge badge-blue" style={{fontSize:'.7rem'}}>Job{p.jobNo||'—'}</span></td>
                              <td>
                                {p.photoPath?<img className="photo-thumb" src={`${BASE_URL}${p.photoPath}`} alt=""style={{borderRadius: p.photoShape==='square'?'8px':'50%'}} onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>:null}
                                <div className="photo-placeholder" style={{display:p.photoPath?'none':'flex'}}>👤</div>
                              </td>
                              <td className="td-bold">{p.name||'—'}</td>
                              {/* <td>{p.class||'—'}</td> */}
                              <td>{p.mobile||'—'}</td>
                              <td><span className={`badge ${STATUS_COLOR[p.status]||'badge-orange'}`}>{p.status}</span></td>
                              <td>
                                <div className="action-btns">
                                  <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`}
                                    onClick={()=>openEdit(p)} style={locked?{opacity:.5}:{}}
                                    title={locked?'Locked':'Edit'}>
                                    {locked?'🔒':'✏️'}
                                  </button>
                                  <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`}
                                    style={locked?{opacity:.5}:{}}
                                    title={locked?'Photo locked':'Take photo with camera'}
                                    onClick={()=>{ if(locked){toast.error('🔒 Photo locked. Ask admin to unlock.');return;} setPhotoTarget(p);setShowCamera(true); }}>
                                    📷
                                  </button>
                                  <button className={`btn btn-sm ${locked?'btn-ghost':'btn-outline'}`}
                                    style={locked?{opacity:.5}:{}}
                                    title={locked?'Locked':'Master images'}
                                    onClick={()=>{ if(locked){toast.error('🔒 Locked.');return;} setMasterTarget(p);setMasterType('sign'); }}>
                                    🖼️
                                  </button>
                                  {!locked && (
                                    <button className="btn btn-danger btn-xs"
                                      onClick={()=>handleDelete(p._id,p.name)} title="Delete">
                                      🗑️
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    
                  </div>
                  </div>
                  <div className="profile-cards-wrap">
      {profiles.map(p => {
        const locked = p.status !== 'unprocessed';
        return (
          <ProfileCard
            key={p._id}
            p={p}
            selected={selectedIds.has(p._id)}
            locked={locked}
            BASE_URL={BASE_URL}
            onSelect={()=>toggleSelect(p._id)}
            onEdit={()=>openEdit(p)}
            onCamera={()=>{ if(locked){toast.error('🔒 Photo locked.');return;} setPhotoTarget(p);setShowCamera(true); }}
            onMaster={()=>{ if(locked){toast.error('🔒 Locked.');return;} setMasterTarget(p);setMasterType('sign'); }}
            onDelete={()=>handleDelete(p._id,p.name)}
          />
        );
      })}
    </div>

                  {!showAll && pages>1 && (
                    <div className="pagination">
                      <button className="page-btn" onClick={()=>setPage(1)} disabled={page===1}>«</button>
                      <button className="page-btn" onClick={()=>setPage(p=>p-1)} disabled={page===1}>‹</button>
                      {Array.from({length:Math.min(pages,7)},(_,i)=>{const n=page<=4?i+1:page-3+i;if(n<1||n>pages)return null;return <button key={n} className={`page-btn${n===page?' current':''}`} onClick={()=>setPage(n)}>{n}</button>;})}
                      <button className="page-btn" onClick={()=>setPage(p=>p+1)} disabled={page===pages}>›</button>
                      <button className="page-btn" onClick={()=>setPage(pages)} disabled={page===pages}>»</button>
                      <span className="page-info">Page {page}/{pages} · {total} total</span>
                    </div>
                  )}
                  {showAll && <div style={{textAlign:'center',padding:'10px 0',color:'var(--text-muted)',fontSize:'.82rem'}}>Showing all {total} profiles — <button className="btn btn-ghost btn-xs" onClick={()=>setShowAll(false)}>Enable paging</button></div>}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ EXCEL IMPORT ═══════════════════════════════════════════════════════ */}
      {tab==='excel' && (
        <div className="card">
          <div className="card-title">📊 Import Profiles from Excel</div>
          <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'16px 18px',marginBottom:18}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
              <div>
                <div style={{fontWeight:700,fontSize:'.9rem',marginBottom:3}}>📥 Download Blank Template</div>
                <div style={{color:'var(--text-muted)',fontSize:'.8rem'}}>Select columns, download template, fill and upload</div>
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setShowTemplate(t=>!t)}>{showTemplate?'▲ Hide':'▼ Select Fields'}</button>
                <button className="btn btn-success btn-sm" onClick={()=>downloadBlankTemplate(templateFields)} disabled={!templateFields.length}>⬇️ Download Template</button>
              </div>
            </div>
            {showTemplate && (
              <div style={{marginTop:14}}>
                <div style={{display:'flex',gap:6,marginBottom:10}}>
                  <button className="btn btn-ghost btn-xs" onClick={()=>setTemplateFields(ALL_FIELDS.map(f=>f.key))}>All</button>
                  <button className="btn btn-ghost btn-xs" onClick={()=>setTemplateFields([])}>None</button>
                  <span style={{color:'var(--text-muted)',fontSize:'.75rem',alignSelf:'center'}}>{templateFields.length} selected</span>
                </div>
                {Object.entries(FIELDS_BY_SECTION).map(([sec,fields])=>(
                  <div key={sec} style={{marginBottom:10}}>
                    <div style={{fontSize:'.7rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--primary)',marginBottom:6}}>{SECTION_ICONS[sec]} {sec}</div>
                    <div className="checkbox-grid">
                      {fields.map(f=><div key={f.key} className="checkbox-item" onClick={()=>toggleTField(f.key)}><input type="checkbox" readOnly checked={templateFields.includes(f.key)}/><label>{f.label}</label></div>)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="instructions">
            <strong>✅ Supported headers — English · Marathi · Urdu</strong>
            <ul>
              <li><b>Personal:</b> Name/नाव/الاسم, PRNCode, DOB, Class/वर्ग, Mobile/मोबाइल, Address/पत्ता, BloodGroup/रक्तगट</li>
              <li><b>Family:</b> FatherName/वडिलांचे नाव, MotherName/आईचे नाव, EmergencyName…</li>
              <li><b>Employee:</b> Designation/पदनाम, EmployeeCode, DOJ, DOR, Department/विभाग</li>
              <li><b>IDs:</b> Aadhar, PAN, RFIDCode, BarCode, QRCode, PhotoName</li>
            </ul>
            <p style={{marginTop:8,color:'var(--warning)',fontWeight:600}}>⚠️ Status auto-set to <b>unprocessed</b> on import</p>
          </div>
       
<div style={{marginBottom:16,padding:'12px 16px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
      <label style={{fontWeight:700,fontSize:'.85rem',display:'block',marginBottom:8}}>
        🏫 Select School for this import <span style={{color:'var(--error)'}}>*</span>
      </label>
      <select className="search-input" style={{maxWidth:300}}
        value={bulkUploadSchool} onChange={e=>setBulkUploadSchool(e.target.value)}>
        <option value="">Select School…</option>
        {schools.map(s=><option key={s._id} value={s.name}>{s.name}</option>)}
      </select>
      {!bulkUploadSchool && <div style={{fontSize:'.76rem',color:'var(--warning)',marginTop:6}}>⚠️ Select a school before importing</div>}
    </div>
          <div className={`drop-zone${excelDrag?' active':''}`} onClick={()=>excelInputRef.current.click()}
            onDragOver={e=>{e.preventDefault();setExcelDrag(true);}} onDragLeave={()=>setExcelDrag(false)} onDrop={handleExcelDrop}>
            <div className="drop-zone-icon">📄</div>
            <h3>Drag & drop or click to select</h3>
            <p>.xlsx or .xls · Marathi / Urdu / English supported</p>
            <input ref={excelInputRef} type="file" accept=".xlsx,.xls" onChange={e=>{setExcelFile(e.target.files[0]);setExcelResult(null);e.target.value='';}}/>
          </div>
          {excelFile && <div className="file-item" style={{marginTop:10}}><span className="file-icon">📊</span><span className="file-name">{excelFile.name}</span><button className="btn btn-ghost btn-xs" onClick={()=>{setExcelFile(null);setExcelResult(null);}}>✕</button></div>}
          {excelLoading && <div className="progress-wrap"><div className="progress-label"><span>Importing…</span><span>{excelProgress}%</span></div><div className="progress-bar-bg"><div className="progress-bar-fill" style={{width:`${excelProgress}%`}}/></div></div>}
          <div style={{display:'flex',gap:8,marginTop:14}}>
            <button className="btn btn-primary" onClick={handleExcelUpload} disabled={excelLoading||!excelFile}>{excelLoading?'⏳ Importing…':'🚀 Import Now'}</button>
          </div>
          {excelResult && (
            <div className={`result-box ${excelResult.ok?'success':'error'}`}>
              {excelResult.ok ? (
                <>
                  <div className="result-stats">
                    <div className="stat"><div className="stat-num" style={{color:'var(--primary)'}}>{excelResult.data.total}</div><div className="stat-label">Total</div></div>
                    <div className="stat"><div className="stat-num" style={{color:'var(--success)'}}>{excelResult.data.inserted}</div><div className="stat-label">Inserted</div></div>
                    <div className="stat"><div className="stat-num" style={{color:'var(--warning)'}}>{excelResult.data.updated}</div><div className="stat-label">Updated</div></div>
                  </div>
                  {excelResult.data.mappedFields?.length>0&&<p className="result-mapped">✅ Mapped: {excelResult.data.mappedFields.join(', ')}</p>}
                  {excelResult.data.unmappedCols?.length>0&&<p className="result-unmapped">⚠️ Ignored: {excelResult.data.unmappedCols.join(', ')}</p>}
                </>
              ) : <p style={{color:'var(--error)',fontWeight:700}}>❌ {excelResult.msg}</p>}
            </div>
          )}
        </div>
      )}

      {tab==='photos' && (
  <div className="card">
    <div className="card-title">🖼️ Bulk Upload</div>
    <div className="instructions">
      <strong>📋 How matching works</strong>
      <ul>
        <li>Filename matched against <b>PRN Code</b>, <b>Photo Name</b>, or <b>Full Name</b></li>
        <li>Works for Photos, Employee Signatures, and Authorized Signature</li>
        {/* <li>Saved in: <code style={{fontFamily:'monospace',background:'var(--bg)',padding:'1px 5px',borderRadius:3}}>{user?.username}_{year}/</code></li> */}
        <li>Formats: JPG, PNG, WEBP · Max 5000 at once</li>
      </ul>
    </div>
{/* <div style={{marginBottom:16,padding:'12px 16px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
      <label style={{fontWeight:700,fontSize:'.85rem',display:'block',marginBottom:8}}>
        📋 Select Job for this upload <span style={{color:'var(--error)'}}>*</span>
      </label>
      <select className="search-input" style={{maxWidth:200}}
        value={bulkUploadJob} onChange={e=>setBulkUploadJob(e.target.value)}>
        <option value="">Select Job No…</option>
        {jobOptions.map(j=><option key={j} value={j}>Job {j}</option>)}
      </select>
      {!bulkUploadJob && <div style={{fontSize:'.76rem',color:'var(--warning)',marginTop:6}}>⚠️ Select a job before uploading</div>}
    </div> */}
    <div style={{marginBottom:16,padding:'12px 16px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
  <label style={{fontWeight:700,fontSize:'.85rem',display:'block',marginBottom:8}}>
    🏫 Select School for this upload <span style={{color:'var(--error)'}}>*</span>
  </label>
  <select className="search-input" style={{maxWidth:300}}
    value={bulkUploadSchool} onChange={e=>setBulkUploadSchool(e.target.value)}>
    <option value="">Select School…</option>
    {schools.map(s=><option key={s._id} value={s.name}>{s.name}</option>)}
  </select>
  {!bulkUploadSchool && <div style={{fontSize:'.76rem',color:'var(--warning)',marginTop:6}}>⚠️ Select a school before uploading</div>}
</div>
    <div className={`drop-zone${bulkDrag?' active':''}`}
      onClick={()=>bulkInputRef.current.click()}
      onDragOver={e=>{e.preventDefault();setBulkDrag(true);}}
      onDragLeave={()=>setBulkDrag(false)}
      onDrop={e=>{e.preventDefault();setBulkDrag(false);addBulkFiles(e.dataTransfer.files);}}>
      <div className="drop-zone-icon">🗂️</div>
      <h3>Drag & drop files here</h3>
      <p>or click to browse · Photos · Employee Signatures · Authorized Signature</p>
      <input ref={bulkInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
        onChange={e=>{addBulkFiles(e.target.files);e.target.value='';}}/>
    </div>

    {bulkFiles.length>0 && (
      <div style={{marginTop:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <span style={{fontWeight:600,fontSize:'.85rem'}}>{bulkFiles.length} file{bulkFiles.length!==1?'s':''} ready</span>
          <button className="btn btn-ghost btn-xs" onClick={()=>{setBulkFiles([]);setBulkResult(null);}}>Clear all</button>
        </div>
        <div style={{maxHeight:200,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
          {bulkFiles.slice(0,50).map((f,i)=>(
            <div key={i} className="file-item">
              <span className="file-icon">🖼️</span>
              <span className="file-name">{f.name}</span>
              <span style={{color:'var(--text-muted)',fontSize:'.74rem',flexShrink:0}}>{fmtSize(f.size)}</span>
              <button className="btn btn-ghost btn-xs" onClick={()=>setBulkFiles(p=>p.filter((_,j)=>j!==i))}>✕</button>
            </div>
          ))}
          {bulkFiles.length>50&&<div style={{textAlign:'center',padding:6,color:'var(--text-muted)',fontSize:'.8rem'}}>…and {bulkFiles.length-50} more</div>}
        </div>
      </div>
    )}

    {bulkLoading&&<div className="progress-wrap" style={{marginTop:12}}><div className="progress-label"><span>Uploading…</span><span>{bulkProgress}%</span></div><div className="progress-bar-bg"><div className="progress-bar-fill" style={{width:`${bulkProgress}%`}}/></div></div>}

    <div style={{marginTop:14}}>
      <button className="btn btn-primary" onClick={handleBulkUpload} disabled={bulkLoading||!bulkFiles.length}>
        {bulkLoading?`⏳ Uploading ${bulkFiles.length}…`:`🚀 Upload ${bulkFiles.length||''} Files`}
      </button>
    </div>

    {bulkResult&&(
      <div className={`result-box ${bulkResult.ok?'success':'error'}`} style={{marginTop:14}}>
        {bulkResult.ok?(
          <>
            <div className="result-stats">
              <div className="stat"><div className="stat-num" style={{color:'var(--primary)'}}>{bulkResult.data.total}</div><div className="stat-label">Total</div></div>
              <div className="stat"><div className="stat-num" style={{color:'var(--success)'}}>{bulkResult.data.saved}</div><div className="stat-label">Saved</div></div>
              <div className="stat"><div className="stat-num" style={{color:'var(--warning)'}}>{bulkResult.data.skipped}</div><div className="stat-label">Unlinked</div></div>
            </div>
            {bulkResult.data.details?.saved?.length>0&&(<><p style={{fontWeight:700,marginBottom:6,marginTop:8,color:'var(--success)',fontSize:'.82rem'}}>✅ Matched:</p><div style={{maxHeight:140,overflowY:'auto'}}>{bulkResult.data.details.saved.map((s,i)=><div key={i} style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:'1px solid rgba(255,255,255,.05)',fontSize:'.78rem'}}><span>{s.file}</span><span style={{color:'var(--text-muted)'}}>{s.name||s.prnCode}</span></div>)}</div></>)}
            {bulkResult.data.details?.skipped?.length>0&&(<><p style={{fontWeight:700,marginBottom:6,marginTop:8,color:'var(--warning)',fontSize:'.82rem'}}>⚠️ Not linked:</p><div style={{maxHeight:100,overflowY:'auto'}}>{bulkResult.data.details.skipped.map((s,i)=><div key={i} style={{padding:'3px 0',fontSize:'.78rem',color:'var(--text-muted)'}}>{s.file}</div>)}</div></>)}
          </>
        ):<p style={{color:'var(--error)',fontWeight:700}}>❌ {bulkResult.msg}</p>}
      </div>
    )}
  </div>
)}

            {/* ══ MY IMAGES TAB ════════════════════════════════════════════════════════ */}
      {tab==='myimages' && (
        <div className="card">
          <div className="card-title">🖼️ My Images for ID Card Template</div>
          <p style={{color:'var(--text-muted)',fontSize:'.84rem',marginBottom:16}}>
            Upload your background image and signature images here. Admin will apply them to the card template.
            Once updated by admin, use <b>🖨️ Cards PDF</b> to export your cards.
          </p>

          {/* Template selector */}
          {myTemplates.length === 0 ? (
            <div style={{background:'var(--bg3)',borderRadius:'var(--radius-sm)',padding:'12px 16px',color:'var(--warning)',fontSize:'.84rem',marginBottom:16}}>
              ⚠️ No card templates created yet. Ask admin to create a template first.
            </div>
          ) : (
            <div style={{marginBottom:20}}>
              <label style={{fontWeight:600,fontSize:'.85rem',display:'block',marginBottom:6}}>Select Template:</label>
              <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                {myTemplates.map(t=>(
                  <div key={t._id}
                    onClick={()=>setSelectedTmplId(t._id)}
                    style={{
                      cursor:'pointer',padding:'8px 14px',borderRadius:'var(--radius-sm)',
                      border:`2px solid ${selectedTmplId===t._id?'var(--primary)':'var(--border)'}`,
                      background: selectedTmplId===t._id?'rgba(var(--primary-rgb),.1)':'var(--bg2)',
                      fontSize:'.84rem',fontWeight:selectedTmplId===t._id?700:400,
                    }}>
                    {t.name} <span style={{fontSize:'.72rem',color:'var(--text-muted)'}}>{t.cardSide}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedTmplId && (() => {
            const tmpl = myTemplates.find(t=>t._id===selectedTmplId);
            if (!tmpl) return null;
            return (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:16}}>

                {/* Background Image */}
                <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:16}}>
                  <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:12}}>🖼️ Background Image</div>
                  <div style={{
                    width:'100%',paddingTop:'63%',position:'relative',marginBottom:12,
                    background: tmpl.bgImagePath
                      ? `url('${BASE_URL}${tmpl.bgImagePath}') center/cover no-repeat`
                      : (tmpl.bgColor||'#f0f0f0'),
                    borderRadius:'var(--radius-sm)',border:'1px solid var(--border)',overflow:'hidden',
                  }}>
                    {!tmpl.bgImagePath && (
                      <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'#aaa',fontSize:'.8rem'}}>No background set</div>
                    )}
                  </div>
                  <p style={{fontSize:'.75rem',color:'var(--text-muted)',marginBottom:10}}>
                    Upload your preferred background. Admin will incorporate it into the template design.
                  </p>
                  <button className="btn btn-outline btn-sm" style={{width:'100%'}}
                    onClick={()=>myBgRef.current.click()} disabled={tmplBgUploading}>
                    {tmplBgUploading?'⏳ Uploading…':'⬆️ Upload Background Image'}
                  </button>
                  <input ref={myBgRef} type="file" accept="image/*" style={{display:'none'}}
                    onChange={async e=>{
                      const file=e.target.files[0]; if(!file)return;
                      setTmplBgUploading(true);
                      try {
                        await uploadTemplateBg(selectedTmplId, file);
                        toast.success('Background uploaded! Admin will apply it to the template.');
                        fetchMyTemplates();
                      } catch { toast.error('Upload failed'); }
                      finally { setTmplBgUploading(false); e.target.value=''; }
                    }}/>
                </div>

                {/* Signature 1 */}
                <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:16}}>
                  <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:12}}>✍️ Employee Signature</div>
                  <div style={{
                    width:'100%',height:80,marginBottom:12,border:'1px solid var(--border)',
                    borderRadius:'var(--radius-sm)',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',
                  }}>
                    {tmpl.elements?.find(e=>e.type==='signature')
                      ? <span style={{fontSize:'.8rem',color:'#aaa'}}>Signature element in template</span>
                      : <span style={{fontSize:'.8rem',color:'#ccc'}}>—</span>
                    }
                  </div>
                  <p style={{fontSize:'.75rem',color:'var(--text-muted)',marginBottom:10}}>
                    Upload via <b>🖼️ button</b> on each profile row in the list (sign type: Signature 1).
                  </p>
                  <button className="btn btn-outline btn-sm" style={{width:'100%'}} onClick={()=>{setTab('list');toast('Use 🖼️ button on a profile row → Signature 1',{icon:'ℹ️'});}}>
                    ← Go to Profile List
                  </button>
                </div>

                {/* Signature 2 */}
                <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:16}}>
                  <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:12}}>✍️ Authorized Signature</div>
                  <div style={{
                    width:'100%',height:80,marginBottom:12,border:'1px solid var(--border)',
                    borderRadius:'var(--radius-sm)',background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',
                  }}>
                    <span style={{fontSize:'.8rem',color:'#ccc'}}>—</span>
                  </div>
                  <p style={{fontSize:'.75rem',color:'var(--text-muted)',marginBottom:10}}>
                    Upload via <b>🖼️ button</b> on each profile row (sign type: Authorized Signature 2).
                  </p>
                  <button className="btn btn-outline btn-sm" style={{width:'100%'}} onClick={()=>{setTab('list');toast('Use 🖼️ button on a profile row → Auth. Signature',{icon:'ℹ️'});}}>
                    ← Go to Profile List
                  </button>
                </div>

              </div>
            );
          })()}

          <div style={{marginTop:20,padding:'12px 16px',background:'rgba(var(--primary-rgb),.06)',border:'1px solid rgba(var(--primary-rgb),.15)',borderRadius:'var(--radius-sm)',fontSize:'.8rem',color:'var(--text-muted)'}}>
            💡 <b>Workflow:</b> Upload images here → Admin updates the template → Come back and use <b>🖨️ Cards PDF</b> to export cards with your images applied.
          </div>
        </div>
      )}

{/* ══ ADD PROFILE ════════════════════════════════════════════════════════ */}
      {tab==='add' && (
        <div className="card">
          <div className="card-title">➕ Add New Profile</div>

          {/* Photo preview */}
          <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:20}}>
            <div style={{position:'relative',flexShrink:0}}>
              {addPhotoPreview
                ? <img src={addPhotoPreview} alt="preview"
                    style={{width:80,height:80,borderRadius:'50%',objectFit:'cover',border:'3px solid var(--primary)',boxShadow:'var(--glow)'}}/>
                : <div style={{width:80,height:80,borderRadius:'50%',background:'var(--bg3)',border:'2px dashed var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'2rem'}}>👤</div>
              }
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setAddShowCamera(true)}>📷 Camera</button>
              <button className="btn btn-outline btn-sm" onClick={()=>addFileRef.current.click()}>🗂️ Choose File</button>
              {addPhotoPreview && <button className="btn btn-ghost btn-sm" onClick={()=>{setAddPhoto(null);setAddPhotoPreview(null);}}>✕ Remove</button>}
              <input ref={addFileRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e=>{if(e.target.files[0])handleAddPhotoFile(e.target.files[0]);e.target.value='';}}/>
            </div>
          </div>

          {/* Form fields */}
          {Object.entries(FIELDS_BY_SECTION).map(([section,fields])=>(
            <div className="form-grid" key={section} style={{marginBottom:4}}>
              <div className="form-section-title">{SECTION_ICONS[section]} {section}</div>
              {fields.map(({key,label,type})=>(
                <div key={key} className={`form-group${type==='textarea'?' span-2':''}`}>
                  <label>{label}</label>
                  {type==='textarea'
                    ? <textarea className="form-input" rows={2} placeholder={label}
                        value={addForm[key]||''} onChange={e=>setAddForm({...addForm,[key]:e.target.value})}/>
                    : <input className="form-input" type={type} placeholder={label}
                        value={addForm[key]||''} onChange={e=>setAddForm({...addForm,[key]:e.target.value})}/>
                  }
                </div>
              ))}
            </div>
          ))}

          <div style={{display:'flex',gap:10,marginTop:16}}>
            <button className="btn btn-primary" onClick={handleAddProfile} disabled={addSaving}>
              {addSaving?'⏳ Creating…':'➕ Create Profile'}
            </button>
            <button className="btn btn-outline" onClick={()=>{setAddForm({});setAddPhoto(null);setAddPhotoPreview(null);}}>
              🗑️ Clear
            </button>
          </div>
        </div>
      )}
      {/* ══ DELIVERY TAB ════════════════════════════════════════════════════════ */}
      {tab==='delivery' && (
        <div className="card">
          <div className="card-title">📦 Delivery Status</div>
          <DeliveryReport
            api={api}
            isAdmin={false}
            usernames={[user?.username]}
            defaultUsername={user?.username}
          />
        </div>
      )}

      {/* ══ CARD EXPORT MODAL ════════════════════════════════════════════════════ */}
      {showCardExport && (
        <CardExportModal
          profiles={profiles}
          jobOptions={jobOptions}
          classOptions={classOptions}
          username={user?.username||''}
          api={api}
          onClose={()=>setShowCardExport(false)}
        />
      )}

      {/* ══ EDIT MODAL ═════════════════════════════════════════════════════════ */}
      {editTarget && (
        <div className="modal-overlay" onClick={()=>setEditTarget(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Edit — {editTarget.name||editTarget.username}</h2>
              <button className="modal-close" onClick={()=>setEditTarget(null)}>✕</button>
            </div>
            <div className="modal-body">
              {Object.entries(FIELDS_BY_SECTION).map(([section,fields])=>(
                <div className="form-grid" key={section} style={{marginBottom:4}}>
                  <div className="form-section-title">{SECTION_ICONS[section]} {section}</div>
                  {fields.map(({key,label,type})=>(
                    <div key={key} className={`form-group${type==='textarea'?' span-2':''}`}>
                      <label>{label}</label>
                      {type==='textarea'
                        ? <textarea className="form-input" rows={2} value={editForm[key]||''} onChange={e=>setEditForm({...editForm,[key]:e.target.value})}/>
                        : <input className="form-input" type={type} value={editForm[key]||''} onChange={e=>setEditForm({...editForm,[key]:e.target.value})}/>
                      }
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setEditTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'⏳ Saving…':'💾 Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ PHOTO CHOICE MODAL ═════════════════════════════════════════════════ */}
      {photoTarget && !showCamera && (
        <div className="modal-overlay" onClick={()=>setPhotoTarget(null)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>📷 Upload Photo</h2><button className="modal-close" onClick={()=>setPhotoTarget(null)}>✕</button></div>
            <div className="modal-body" style={{textAlign:'center',padding:'24px 28px 28px'}}>
              {photoTarget.photoPath && <img src={`${BASE_URL}${photoTarget.photoPath}`} alt="current" style={{width:90,height:90,borderRadius:'50%',objectFit:'cover',border:'3px solid var(--primary)',boxShadow:'var(--glow)',display:'block',margin:'0 auto 16px'}}/>}
              <p style={{fontWeight:700,marginBottom:4}}>{photoTarget.name||photoTarget.username}</p>
              <p style={{color:'var(--text-muted)',fontSize:'.76rem',marginBottom:20}}>📁 Saves to: <code style={{fontFamily:'monospace',background:'var(--bg3)',padding:'1px 5px',borderRadius:4}}>{user?.username}_{year}/</code></p>
              {photoUploading
                ? <div className="loading" style={{padding:12}}><div className="spinner"/><p style={{marginTop:10,fontSize:'.84rem'}}>Uploading…</p></div>
                : <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
                    <button className="btn btn-primary" style={{minWidth:150}} onClick={()=>setShowCamera(true)}>📷 Open Camera</button>
                    <button className="btn btn-outline" style={{minWidth:150}} onClick={()=>filePhotoRef.current.click()}>🗂️ Choose from Device</button>
                    {/* <input ref={filePhotoRef} type="file" accept="image/*" style={{display:'none'}} onChange={e=>{if(e.target.files[0])doUploadPhoto(e.target.files[0]);}}/> */}
                    <input ref={filePhotoRef} type="file" accept="image/*" style={{display:'none'}} 
  onChange={e=>{
    if(e.target.files[0]) doUploadPhoto(e.target.files[0], 'circle', true); // ← add 3rd arg
  }}/>
                  </div>
              }
            </div>
          </div>
        </div>
      )}

      {/* ══ MASTER IMAGES MODAL ════════════════════════════════════════════════ */}
      {masterTarget && (
        <div className="modal-overlay" onClick={()=>setMasterTarget(null)}>
          <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h2>🖼️ Images — {masterTarget.name}</h2><button className="modal-close" onClick={()=>setMasterTarget(null)}>✕</button></div>
            <div className="modal-body" style={{padding:'20px 24px'}}>
              {/* Sign image */}
              <div style={{marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>✍️ Employee Signature</div>
                {masterTarget.signName && <div style={{fontSize:'.74rem',color:'var(--text-muted)',marginBottom:8}}>📄 {masterTarget.signName}</div>}
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {masterTarget.signImagePath
                    ? <img src={`${BASE_URL}${masterTarget.signImagePath}`} alt="sign" style={{width:80,height:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,background:'white'}}/>
                    : <div style={{width:80,height:60,border:'2px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem'}}>✍️</div>
                  }
                  <div>
                    <button className="btn btn-outline btn-sm" onClick={()=>masterFileRef.current.click()}>
                      {masterTarget.signImagePath?'🔄 Replace':'⬆️ Upload'} Signature
                    </button>
                  </div>
                </div>
              </div>
              {/* Ref image */}
              {/* Signature 2 */}
              <div style={{marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:4}}>🏛️ Authorized Signature</div>
                {masterTarget.authSignName && <div style={{fontSize:'.74rem',color:'var(--text-muted)',marginBottom:8}}>📄 {masterTarget.authSignName}</div>}
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {masterTarget.signImagePath2
                    ? <img src={`${BASE_URL}${masterTarget.signImagePath2}`} alt="sign2" style={{width:80,height:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,background:'white'}}/>
                    : <div style={{width:80,height:60,border:'2px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem'}}>🏛️</div>
                  }
                  <button className="btn btn-outline btn-sm" onClick={()=>{ setMasterType('sign2'); masterFileRef.current.click(); }}>
                    {masterTarget.signImagePath2?'🔄 Replace':'⬆️ Upload'} Auth. Signature
                  </button>
                </div>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'.88rem',marginBottom:10}}>🎨 Reference Design Image</div>
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  {masterTarget.refImagePath
                    ? <img src={`${BASE_URL}${masterTarget.refImagePath}`} alt="ref" style={{width:80,height:60,objectFit:'contain',border:'1px solid var(--border)',borderRadius:6,background:'white'}}/>
                    : <div style={{width:80,height:60,border:'2px dashed var(--border)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.4rem'}}>🎨</div>
                  }
                  <div>
                    <button className="btn btn-outline btn-sm" onClick={()=>{ setMasterType('ref'); masterFileRef.current.click(); }}>
                      {masterTarget.refImagePath?'🔄 Replace':'⬆️ Upload'} Reference
                    </button>
                  </div>
                </div>
              </div>
              <input ref={masterFileRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e=>{ if(e.target.files[0]){ doUploadMaster(e.target.files[0],masterType); e.target.value=''; }}}/>
              {masterUploading && <div className="loading" style={{padding:12,marginTop:12}}><div className="spinner"/><p style={{marginTop:8,fontSize:'.84rem'}}>Uploading…</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* ══ CAMERA ══════════════════════════════════════════════════════════════ */}
      {showCamera && photoTarget && 
      // <CameraModal onCapture={file=>{setShowCamera(false);doUploadPhoto(file);}} onClose={()=>setShowCamera(false)}/>
       <CameraModal
    onCapture={(file, photoShape) => {
      setShowCamera(false);
      doUploadPhoto(file, photoShape);
    }}
    onClose={()=>setShowCamera(false)}
  /> }
      {addShowCamera && 
      // <CameraModal onCapture={file=>{setAddShowCamera(false);handleAddPhotoFile(file);}} onClose={()=>setAddShowCamera(false)}/>
     <CameraModal
    onCapture={(file, photoShape) => {
      setAddShowCamera(false);
      handleAddPhotoFile(file, photoShape);
    }}
    onClose={()=>setAddShowCamera(false)}
  /> }

      {/* ══ SCHOOL MODAL ════════════════════════════════════════════════════════ */}
      {showSchoolModal && (
        <div className="modal-overlay" onClick={()=>{setShowSchoolModal(false);setEditingSchool(null);}}>
          <div className="modal" style={{maxWidth:580}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏫 Manage Schools</h2>
              <button className="modal-close" onClick={()=>{setShowSchoolModal(false);setEditingSchool(null);}}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'var(--bg2)',padding:16,borderRadius:'var(--radius)',marginBottom:18}}>
                <h3 style={{marginBottom:12,fontSize:'1rem'}}>{editingSchool?'Edit School':'Add New School'}</h3>
                <div className="form-grid" style={{marginBottom:12}}>
                  <div className="form-group">
                    <label>School Name *</label>
                    <input className="form-input" value={schoolForm.name} placeholder="School name"
                      onChange={e=>setSchoolForm({...schoolForm,name:e.target.value})}/>
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input className="form-input" value={schoolForm.city} placeholder="City"
                      onChange={e=>setSchoolForm({...schoolForm,city:e.target.value})}/>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input className="form-input" value={schoolForm.address} placeholder="Address"
                      onChange={e=>setSchoolForm({...schoolForm,address:e.target.value})}/>
                  </div>
                  <div className="form-group">
                    <label>Contact No</label>
                    <input className="form-input" value={schoolForm.contactNo} placeholder="Contact number"
                      onChange={e=>setSchoolForm({...schoolForm,contactNo:e.target.value})}/>
                  </div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveSchool} disabled={schoolSaving}>
                    {schoolSaving?'⏳ Saving…':editingSchool?'💾 Update':'➕ Add School'}
                  </button>
                  {editingSchool && (
                    <button className="btn btn-outline btn-sm" onClick={()=>{setEditingSchool(null);setSchoolForm({name:'',address:'',city:'',contactNo:''});}}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              <div style={{fontWeight:700,marginBottom:10,fontSize:'.9rem'}}>Your Schools ({schools.length})</div>
              {schools.length===0 ? (
                <p style={{color:'var(--text-muted)',textAlign:'center',padding:20,fontSize:'.84rem'}}>No schools yet. Add one above.</p>
              ) : (
                <div style={{maxHeight:280,overflowY:'auto'}}>
                  {schools.map(s=>(
                    <div key={s._id} style={{padding:'10px 12px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div>
                        <div style={{fontWeight:600,fontSize:'.88rem'}}>{s.name}</div>
                        <div style={{fontSize:'.74rem',color:'var(--text-muted)',marginTop:2}}>
                          {[s.city,s.address,s.contactNo&&'📞 '+s.contactNo].filter(Boolean).join(' · ')||'No details'}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:6,flexShrink:0}}>
                        <button className="btn btn-ghost btn-xs" onClick={()=>{setEditingSchool(s);setSchoolForm({name:s.name,address:s.address||'',city:s.city||'',contactNo:s.contactNo||''});}}>✏️</button>
                        <button className="btn btn-danger btn-xs" onClick={()=>handleDeleteSchool(s._id,s.name)}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ UPLOAD TOAST ════════════════════════════════════════════════════════ */}
      {uploadToast && <UploadToast label={uploadToast.label} status={uploadToast.status} progress={uploadToast.progress} detail={uploadToast.detail} onClose={()=>setUploadToast(null)}/>}

      {/* ══ EXPORT MODAL ════════════════════════════════════════════════════════ */}
      {showExport && (
        <div className="modal-overlay" onClick={()=>setShowExport(false)}>
          <div className="modal" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>⬇️ Export — {selectedIds.size>0?`${selectedIds.size} Selected`:'All'} Profiles</h2>
              <button className="modal-close" onClick={()=>setShowExport(false)}>✕</button>
            </div>
            <div className="modal-body">
              {selectedIds.size>0
                ? <div style={{background:'rgba(var(--primary-rgb),.1)',border:'1px solid rgba(var(--primary-rgb),.3)',borderRadius:'var(--radius-sm)',padding:'8px 12px',marginBottom:12,fontSize:'.82rem',color:'var(--primary)',display:'flex',alignItems:'center',justifyContent:'space-between'}}><span>✅ Exporting <b>{selectedIds.size}</b> selected profile{selectedIds.size!==1?'s':''}</span><button className="btn btn-ghost btn-xs" onClick={()=>setSelectedIds(new Set())}>Clear</button></div>
                : <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'8px 12px',marginBottom:12,fontSize:'.82rem',color:'var(--text-muted)'}}>📋 Exporting <b>all</b> profiles — select rows to export specific ones</div>
              }
              <div style={{display:'flex',gap:8,marginBottom:14,alignItems:'center'}}>
                <button className="btn btn-outline btn-sm" onClick={()=>setExportFields(ALL_FIELDS.map(f=>f.key))}>Select All Fields</button>
                <button className="btn btn-outline btn-sm" onClick={()=>setExportFields([])}>Clear All Fields</button>
                <span style={{color:'var(--text-muted)',fontSize:'.78rem'}}>{exportFields.length}/{ALL_FIELDS.length}</span>
              </div>
              <div style={{marginBottom:14,padding:'8px 12px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}>
  <label style={{display:'flex',alignItems:'center',gap:8,cursor:selectedIds.size>0?'pointer':'not-allowed',opacity:selectedIds.size>0?1:.5,fontSize:'.84rem'}}>
    <input type="checkbox" checked={includePhotos} disabled={selectedIds.size===0}
      onChange={e=>setIncludePhotos(e.target.checked)}/>
    📦 Include Photos (downloads as ZIP)
  </label>
  {selectedIds.size===0 && <div style={{fontSize:'.72rem',color:'var(--text-muted)',marginTop:4}}>Select specific profiles first to enable this option</div>}
</div>
              {Object.entries(FIELDS_BY_SECTION).map(([sec,fields])=>(
                <div key={sec} style={{marginBottom:14}}>
                  <div style={{fontSize:'.7rem',fontWeight:800,textTransform:'uppercase',letterSpacing:'.07em',color:'var(--primary)',marginBottom:8}}>{SECTION_ICONS[sec]} {sec}</div>
                  <div className="checkbox-grid">
                    {fields.map(f=><div key={f.key} className="checkbox-item" onClick={()=>toggleEField(f.key)}><input type="checkbox" readOnly checked={exportFields.includes(f.key)}/><label>{f.label}</label></div>)}
                  </div>
                </div>
              ))}
            </div>
            {/* <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setShowExport(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleExport} disabled={exportingExcel||!exportFields.length}>{exportingExcel?'⏳ Exporting…':`⬇️ Download (${exportFields.length} cols)`}</button>
            </div> */}
            <div className="modal-footer">
  <button className="btn btn-outline" onClick={()=>setShowExport(false)}>Cancel</button>
  {/* <button className="btn btn-primary" onClick={handleExport} disabled={exportingExcel||!exportFields.length}>
    {exportingExcel ? '⏳ Exporting…' : (includePhotos && selectedIds.size>0 ? `📦 Download ZIP (${exportFields.length} cols + photos)` : `⬇️ Download (${exportFields.length} cols)`)}
  </button> */}

  <button
  className="btn btn-primary"
  onClick={handleExport}
  disabled={
    exportingExcel ||
    !exportFields.length ||
    selectedIds.size === 0   // 👈 new condition
  }
>
  {exportingExcel
    ? '⏳ Exporting…'
    : (includePhotos && selectedIds.size > 0
        ? `📦 Download ZIP (${exportFields.length} cols + photos)`
        : `⬇️ Download (${exportFields.length} cols)`)}
</button>
</div>
          </div>
        </div>
      )}
    </div>
  );
}
